import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  plotterActions,
  PATH_MODES,
  LabelDisplayOption,
  MAX_TSPS_LABELS_THRESHOLD,
} from '../plotter/plotterSlice';
import { useLocalStorage } from '../helpers/useLocalStorage';
import { useLazyGetRiseTransportPathOnDateQuery } from '../services/edgeApi';
import { toIsoDate } from '../utils/stringUtils';
import toast from 'react-hot-toast';
import {
  selectLabelDisplayOptions,
  selectLayers,
  selectNodeSize,
  selectPathMode,
  selectShowConstraints,
  selectShowEdgeDirections,
  selectShowFlowArrows,
  selectShowNoticeMarkers,
} from './plotterSelectors';
import { MultiSelectButton } from '../helpers/MultiSelect';
import { useViewMode } from '../hooks/useViewMode';
import { DevelopmentFlag } from '../helpers/DevelopmentFlag';
import { ChevronDownIcon } from '@iconicicons/react';
import { selectFilteredTsps } from '../map/slice/mapSelectors';
import { Collapse } from 'bootstrap';

enum PATH_MODE {
  ANT = 'ANT',
  SIMPLE = 'SIMPLE',
}

export const PlotterSettings = () => {
  const dispatch = useDispatch();
  const labelSettingsRef = useRef<HTMLDivElement>(null);
  const [riseTransportsTrigger] = useLazyGetRiseTransportPathOnDateQuery();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentRiseDateRef = useRef<string>('');

  const _showFlowArrows = useSelector(selectShowFlowArrows);
  const [showFlowArrows, setShowFlowArrows] = useLocalStorage(
    'showFlowArrows',
    _showFlowArrows,
  );

  const _showEdgeDirections = useSelector(selectShowEdgeDirections);
  const [showEdgeDirections, setShowEdgeDirections] = useLocalStorage(
    'showEdgeDirections',
    _showEdgeDirections,
  );

  const _showNoticeMarkers = useSelector(selectShowNoticeMarkers);
  const [showNoticeMarkers, setShowNoticeMarkers] = useLocalStorage(
    'showNoticeMarkers',
    _showNoticeMarkers,
  );

  const _showConstraints = useSelector(selectShowConstraints);
  const [showConstraints, setShowConstraints] = useLocalStorage(
    'showConstraints',
    _showConstraints,
  );

  const _pathMode = useSelector(selectPathMode);
  const _nodeSize = useSelector(selectNodeSize);
  const [nodeSize, setNodeSize] = useLocalStorage('plotterNodeSize', _nodeSize);
  const [pathMode, setPathMode] = useLocalStorage<PATH_MODE>(
    'showPaths',
    _pathMode as PATH_MODE,
  );
  const [riseFlowDate, setRiseFlowDate] = useState(toIsoDate(new Date()));
  const [showRiseFlows, setShowRiseFlows] = useLocalStorage(
    'showRiseFlows',
    false,
  );
  const _layers = useSelector(selectLayers);
  const [layers, setLayers] = useLocalStorage('mapLayers', _layers);

  const _labelDisplayOptions = useSelector(selectLabelDisplayOptions);
  const [labelDisplayOptions, setLabelDisplayOptions] = useLocalStorage(
    'plotterLabelDisplayOptions',
    _labelDisplayOptions,
  );
  const filteredTsps = useSelector(selectFilteredTsps);

  const areTspsFiltered = filteredTsps.length < MAX_TSPS_LABELS_THRESHOLD;

  const [viewMode] = useViewMode();

  useEffect(() => {
    dispatch(plotterActions.setShowFlowArrows(showFlowArrows));
    dispatch(plotterActions.setShowEdgeDirections(showEdgeDirections));
    dispatch(plotterActions.setShowNoticeMarkers(showNoticeMarkers));
    dispatch(plotterActions.setShowConstraints(showConstraints));
    dispatch(plotterActions.setNodeSize(nodeSize));
    dispatch(plotterActions.setPathMode(pathMode));
    dispatch(plotterActions.setShowRiseChains(showRiseFlows));
    Object.entries(layers).forEach(([layer, value]) => {
      dispatch(plotterActions.setLayers({ layer, value }));
    });
    dispatch(
      plotterActions.setLabelDisplayOption({
        labelType: 'points',
        value: labelDisplayOptions.points,
      }),
    );
    dispatch(
      plotterActions.setLabelDisplayOption({
        labelType: 'compressors',
        value: labelDisplayOptions.compressors,
      }),
    );
    dispatch(
      plotterActions.setLabelDisplayOption({
        labelType: 'interconnects',
        value: labelDisplayOptions.interconnects,
      }),
    );
  }, []);

  const toggleShowFlowArrows = () => {
    dispatch(plotterActions.setShowFlowArrows(!showFlowArrows));
    setShowFlowArrows(!showFlowArrows);
  };

  const toggleShowEdgeDirections = () => {
    dispatch(plotterActions.setShowEdgeDirections(!showEdgeDirections));
    setShowEdgeDirections(!showEdgeDirections);
  };

  const toggleShowNoticeMarkers = () => {
    dispatch(plotterActions.setShowNoticeMarkers(!showNoticeMarkers));
    setShowNoticeMarkers(!showNoticeMarkers);
  };

  const toggleShowConstraints = () => {
    dispatch(plotterActions.setShowConstraints(!showConstraints));
    setShowConstraints(!showConstraints);
  };

  const onPathModeChange = (pathMode: PATH_MODE) => {
    dispatch(plotterActions.setPathMode(pathMode));
    setPathMode(pathMode);
  };

  const onShowRiseFlowsChange = (showRiseFlows: boolean) => {
    dispatch(plotterActions.setShowRiseChains(showRiseFlows));
    setShowRiseFlows(showRiseFlows);
  };

  const toggleLayer = (layer: keyof typeof layers) => {
    const newLayers = { ...layers, [layer]: !layers[layer] };
    dispatch(plotterActions.setLayers({ layer, value: newLayers[layer] }));
    setLayers(newLayers);
    return newLayers[layer];
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!showRiseFlows || riseFlowDate === currentRiseDateRef.current) {
      return;
    }
    currentRiseDateRef.current = riseFlowDate;
    timeoutRef.current = setTimeout(async () => {
      dispatch(plotterActions.setRiseChainsLoading(true));
      const response = await riseTransportsTrigger({
        gasDate: toIsoDate(new Date(riseFlowDate)),
      });
      dispatch(plotterActions.setRiseChainsLoading(false));
      if (response.error) {
        toast.error('Failed to fetch rise paths');
        dispatch(plotterActions.setRiseChains([]));
        return;
      }
      const transports = response.data;
      dispatch(
        plotterActions.setRiseChains(
          transports?.map((t) => {
            return { ...t, visible: true };
          }) ?? [],
        ),
      );
    }, 1500);
  }, [riseFlowDate, showRiseFlows]);

  const layerOptions: ['counties', 'temperature', 'precipitation'] = useMemo(
    () => ['counties', 'temperature', 'precipitation'],
    [],
  );
  const mapElements: Array<{
    label: string;
    toggle: () => void;
    getter: () => boolean;
  }> = useMemo(
    () => [
      {
        label: 'Flow Arrows',
        toggle: toggleShowFlowArrows,
        getter: () => showFlowArrows,
      },
      {
        label: 'Segment Directions',
        toggle: toggleShowEdgeDirections,
        getter: () => showEdgeDirections,
      },
      {
        label: 'Notice Markers',
        toggle: toggleShowNoticeMarkers,
        getter: () => showNoticeMarkers,
      },
      {
        label: 'Constraints',
        toggle: toggleShowConstraints,
        getter: () => showConstraints,
      },
    ],
    [showFlowArrows, showEdgeDirections, showNoticeMarkers, showConstraints],
  );

  return (
    <div className="row mx-0 w-100">
      <h6 className="text-center">View Settings</h6>
      <div className="col-12">
        <label>Point Size</label>
        <input
          onChange={(evt) => {
            setNodeSize(Number(evt.target.value));
            dispatch(plotterActions.setNodeSize(Number(evt.target.value)));
          }}
          type="range"
          className="form-range"
          value={nodeSize}
          min="0"
          max="14"
          step={0.25}
          id="customRange2"
        />
      </div>
      <div className="col-6">
        <MultiSelectButton
          className="w-100"
          options={layerOptions}
          onToggle={(value) => toggleLayer(value)}
          title="Layers"
          isMarked={(value) => layers[value]}
          keyExtractor={(value) => value}
          optionRenderer={(value) => value}
        />
      </div>
      <div className="col-6">
        <MultiSelectButton
          className="w-100"
          options={mapElements}
          onToggle={(element) => element.toggle()}
          title="Elements"
          isMarked={(element) => element.getter()}
          keyExtractor={(element) => element.label}
          optionRenderer={(element) => element.label}
        />
      </div>
      {viewMode === 'development' && (
        <div className="mt-3 col-12">
          <div className="input-group">
            <label className="input-group-text" htmlFor="path-mode-select">
              <DevelopmentFlag className="me-2" />
              Path Mode
            </label>
            <select
              className="form-select"
              id="path-mode-select"
              value={pathMode}
              onChange={(e) => onPathModeChange(e.target.value as PATH_MODE)}
            >
              <option value={PATH_MODES.ANT}>Ant Paths</option>
              <option value={PATH_MODES.SIMPLE}>Simple Paths</option>
            </select>
          </div>
        </div>
      )}
      {viewMode === 'development' && (
        <div className="mt-3 col-12">
          <div className="input-group">
            <label
              className="input-group-text"
              htmlFor="rise-flow-date"
              onClick={() => onShowRiseFlowsChange(!showRiseFlows)}
            >
              <DevelopmentFlag className="me-2" /> Show Rise Flows
              <input
                checked={showRiseFlows}
                className="form-check-input ms-3"
                type="checkbox"
                onChange={() => null}
              />
            </label>
            <input
              className="form-control"
              id="rise-flow-date"
              type="date"
              value={riseFlowDate}
              onChange={(e) => setRiseFlowDate(e.target.value)}
            />
          </div>
        </div>
      )}
      <div className="mt-3 col-12">
        <span
          className="form-label"
          role="button"
          onClick={() => {
            if (!labelSettingsRef.current) return;
            const bsCollapse = new Collapse(labelSettingsRef.current, {
              toggle: true,
            });
            bsCollapse.toggle();
          }}
        >
          Label Settings <ChevronDownIcon className="ms-2" />
        </span>
        {areTspsFiltered === false && (
          <p className="text-muted">
            Too many pipelines are being displayed to show labels, please filter
            to up to {MAX_TSPS_LABELS_THRESHOLD} pipelines to see location
            names.
          </p>
        )}
        <div className="collapse" id="label-settings" ref={labelSettingsRef}>
          <div className="d-flex flex-column gap-2">
            <div className="form-floating">
              <select
                disabled={!areTspsFiltered}
                className="form-select"
                id="label-mode-select"
                value={areTspsFiltered ? labelDisplayOptions.points : 'HIDE'}
                onChange={(e) => {
                  setLabelDisplayOptions((p) => ({
                    ...p,
                    points: e.target.value as LabelDisplayOption,
                  }));

                  dispatch(
                    plotterActions.setLabelDisplayOption({
                      labelType: 'points',
                      value: e.target.value as LabelDisplayOption,
                    }),
                  );
                }}
              >
                <option value="AUTO">Dynamic</option>
                <option value="SHOW">Always Show</option>
                <option value="HIDE">Always Hide</option>
              </select>
              <label htmlFor="label-mode-select">Points</label>
            </div>
            <div className="form-floating">
              <select
                disabled={!areTspsFiltered}
                className="form-select"
                id="label-mode-select"
                value={
                  areTspsFiltered ? labelDisplayOptions.compressors : 'HIDE'
                }
                onChange={(e) => {
                  setLabelDisplayOptions((p) => ({
                    ...p,
                    compressors: e.target.value as LabelDisplayOption,
                  }));
                  dispatch(
                    plotterActions.setLabelDisplayOption({
                      labelType: 'compressors',
                      value: e.target.value as LabelDisplayOption,
                    }),
                  );
                }}
              >
                <option value="AUTO">Dynamic</option>
                <option value="SHOW">Always Show</option>
                <option value="HIDE">Always Hide</option>
              </select>
              <label htmlFor="label-mode-select">Compressors</label>
            </div>
            <div className="form-floating">
              <select
                disabled={!areTspsFiltered}
                className="form-select"
                id="label-mode-select"
                value={
                  areTspsFiltered ? labelDisplayOptions.interconnects : 'HIDE'
                }
                onChange={(e) => {
                  setLabelDisplayOptions((p) => ({
                    ...p,
                    interconnects: e.target.value as LabelDisplayOption,
                  }));
                  dispatch(
                    plotterActions.setLabelDisplayOption({
                      labelType: 'interconnects',
                      value: e.target.value as LabelDisplayOption,
                    }),
                  );
                }}
              >
                <option value="AUTO">Dynamic</option>
                <option value="SHOW">Always Show</option>
                <option value="HIDE">Always Hide</option>
              </select>
              <label htmlFor="label-mode-select">Interconnects</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
